'use client'

import { MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Badge, Cell, Divider, Icon, Image } from '@vinted/web-ui'
import { ArrowLeft24, ChevronRight24, Dots24 } from '@vinted/monochrome-icons'
import { compact } from 'lodash'
import { v4 as uuid } from 'uuid'
import { InView } from 'react-intersection-observer'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  CatalogNavigationModel,
  CatalogItem,
  CatalogItemData,
  CatalogAttribute,
  ROOT_CATALOG_ID,
  WEB_CATALOG_ROOT_ALL_CODE,
} from '@marketplace-web/catalog/catalog-data'

import { selectCatalogEvent } from '@marketplace-web/catalog/catalog-page-data'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'
import { getSearchSessionData } from '@marketplace-web/search/search-feature'

import useCatalogFilter from '../../../../hooks/useCatalogFilter'
import SelectableItemList, { RenderItemProps } from '../../common/SelectableItemList'
import FilterCell from '../../common/FilterCell'
import FilterModal from '../../common/FilterModal'
import FilterModalNavigation from '../../common/FilterModalNavigation'
import FilterCellSuffix from '../../common/FilterCell/FilterCellSuffix'

type Props = {
  allCatalogs: Record<number, CatalogNavigationModel>
  selectedCatalogsIds: Array<number>
  isFilterCellVisible: boolean
  onCatalogSelect: (newCatalogIds: Array<number>) => void
  onSubmit: () => void
  onFilterCellClick: () => void
  onFilterCellView: () => void
  onModalOpen: () => void
  onModalClose: () => void
}

const CatalogFilter = ({
  allCatalogs,
  selectedCatalogsIds,
  isFilterCellVisible,
  onCatalogSelect,
  onSubmit,
  onFilterCellClick,
  onFilterCellView,
  onModalOpen,
  onModalClose,
}: Props) => {
  const translate = useTranslate('catalog.filters')
  const { track } = useTracking()
  const [openedCatalogsIds, setOpenedCatalogsIds] = useState<Array<number>>([])

  const { allShownCatalogs } = useCatalogFilter(
    openedCatalogsIds,
    translate('catalog.all'),
    allCatalogs,
  )

  const getParentIds = useCallback(
    (catalog: CatalogItemData, ids: Array<number> = []) => {
      const parent = allCatalogs[catalog?.parentId ?? '']

      if (!parent) return ids

      return getParentIds(parent, [...ids, parent.id])
    },
    [allCatalogs],
  )

  const seenItems = useRef(new Set<number>())
  const filteringSessionId = useRef(uuid())

  useEffect(() => {
    seenItems.current.clear()

    if (openedCatalogsIds.length === 0) {
      filteringSessionId.current = uuid()
    }
  }, [openedCatalogsIds])

  const closeModal = (catalogId: number) => {
    setOpenedCatalogsIds(prevIds => prevIds.filter(prevId => prevId !== catalogId))
  }

  const openModal = (catalogId: number) => {
    setOpenedCatalogsIds(prevIds => [...prevIds, catalogId])
  }

  const closeAllModals = () => {
    setOpenedCatalogsIds([])
    onModalClose()
  }

  const handleInView = (catalog: CatalogItemData, index: number) => {
    if (seenItems.current.has(catalog.id)) return
    seenItems.current.add(catalog.id)

    const { searchSessionId, globalSearchSessionId, globalCatalogBrowseSessionId } =
      getSearchSessionData()

    track(
      userViewDynamicFilterOptionEvent({
        id: catalog.id,
        code: 'catalog',
        type: 'catalog',
        fromScreen: 'catalog',
        position: index + 1,
        filteringSessionId: filteringSessionId.current,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const trackCatalogClick = (catalog: CatalogItemData, index: number) => {
    const { globalSearchSessionId, searchSessionId, globalCatalogBrowseSessionId } =
      getSearchSessionData()

    let isSelected = false
    if (catalog.catalogIds.length === 0) {
      isSelected = !selectedCatalogsIds.includes(catalog.id)
    }

    track(
      userClickDynamicFilterOptionEvent({
        id: catalog.id,
        isSelected,
        code: 'catalog',
        type: 'catalog',
        fromScreen: 'catalog',
        position: index + 1,
        filteringSessionId: filteringSessionId.current,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const handleModalClose = (catalogId: number) => () => {
    if (catalogId === ROOT_CATALOG_ID) onModalClose()
    closeModal(catalogId)
  }

  const handleSubmitClick = () => {
    closeAllModals()
    onSubmit()
  }

  const clearSelectedCatalogs = (catalogId: number) => () => {
    let newSelectedIds: Array<number> = []

    if (catalogId !== ROOT_CATALOG_ID) {
      newSelectedIds = selectedCatalogsIds.filter(
        setId => ![setId, ...getParentIds(allCatalogs[setId]!)].includes(catalogId),
      )
    }

    onCatalogSelect(newSelectedIds)
  }

  const trackCatalogSelection = (selectedCatalogIds: Array<number>) => {
    track(
      selectCatalogEvent({
        catalogIds: selectedCatalogIds,
        attributeId: CatalogAttribute.Filter,
        screen: 'filter_catalog_selection',
      }),
    )
  }

  const handleCatalogClick = (catalog: CatalogItem, _: MouseEvent, index: number) => {
    if (!catalog.data) return

    const { id: catalogId, catalogIds, code } = catalog.data

    trackCatalogClick(catalog.data, index)

    if (catalogIds.length > 0 && code !== WEB_CATALOG_ROOT_ALL_CODE) {
      openModal(catalogId)

      return
    }

    let newSelectedIds: Array<number> = []

    if (selectedCatalogsIds.includes(catalogId)) {
      newSelectedIds = selectedCatalogsIds.filter(setId => setId !== catalogId)
    } else {
      newSelectedIds = [catalogId]
    }

    trackCatalogSelection(newSelectedIds)
    onCatalogSelect(newSelectedIds)
  }

  const handleFilterCellClick = () => {
    openModal(ROOT_CATALOG_ID)
    onFilterCellClick()
    onModalOpen()
  }

  const renderIcon = (photo: CatalogNavigationModel['photo'], code: string, depth: number) => {
    if (depth > 1) return null
    if (code === WEB_CATALOG_ROOT_ALL_CODE) {
      return <Icon name={Dots24} color={Icon.Color.Primary} />
    }
    if (!photo) return null

    return <Image src={photo.url} size={Image.Size.Regular} />
  }

  const renderCatalogSuffix = (isNavigation: boolean, suffix: ReactNode) => () => {
    if (isNavigation) return <Icon name={ChevronRight24} color={Icon.Color.GreyscaleLevel3} />

    return suffix
  }

  const getCatalogSuffixText = (catalogId: number) => {
    let selectedChildrenIds: Array<number> = selectedCatalogsIds

    if (catalogId !== ROOT_CATALOG_ID) {
      selectedChildrenIds = selectedCatalogsIds.filter(setId =>
        [setId, ...getParentIds(allCatalogs[setId]!)].includes(catalogId),
      )
    }

    return compact(selectedChildrenIds.map(id => allCatalogs[id]?.title)).join(', ')
  }

  const renderCatalog = (
    { item, itemElementProps }: RenderItemProps<CatalogItemData>,
    index: number,
  ) => {
    if (!item.data) return null

    const { id, catalogIds, code, depth, photo, badge } = item.data
    const isNavigation = catalogIds.length > 0 && code !== WEB_CATALOG_ROOT_ALL_CODE
    const badgeElement = isNavigation && badge && (
      <Badge theme={badge.theme || 'primary'} content={badge.title} />
    )

    const { key: cellKey, ...restItemElementProps } =
      itemElementProps as typeof itemElementProps & {
        key: string
      }

    return (
      <InView
        key={item.value}
        onChange={inView => inView && item.data && handleInView(item.data, index)}
      >
        <Cell
          key={cellKey}
          {...restItemElementProps}
          prefix={renderIcon(photo, code, depth)}
          suffix={
            <FilterCellSuffix
              suffixText={isNavigation && getCatalogSuffixText(id)}
              textTheme="primary"
              renderSuffix={renderCatalogSuffix(isNavigation, itemElementProps.suffix)}
              badge={badgeElement}
            />
          }
        />
      </InView>
    )
  }

  const renderModalNavigation = (catalogId: number, title: string) => () => (
    <FilterModalNavigation
      title={title}
      icon={
        <Icon
          name={ArrowLeft24}
          color={Icon.Color.GreyscaleLevel1}
          testId={`${title}-navigation-prefix-icon`}
        />
      }
      prefixAriaLabel={translate('a11y.actions.back')}
      suffixText={translate('dynamic.clear')}
      onPrefixClick={handleModalClose(catalogId)}
      onSuffixClick={clearSelectedCatalogs(catalogId)}
    />
  )

  const renderCatalogFilterModal = (catalogId: number) => {
    const shownCatalogs = allShownCatalogs[catalogId]

    if (!shownCatalogs?.length) return null

    const title =
      catalogId === ROOT_CATALOG_ID ? translate('catalog.name') : allCatalogs[catalogId]!.title

    return (
      <FilterModal
        renderNavigation={renderModalNavigation(catalogId, title)}
        onSubmit={handleSubmitClick}
        isOpen
        key={catalogId}
        testId={`${title}-catalog-modal`}
      >
        <SelectableItemList<CatalogItemData, number>
          name={`${title}_catalog_ids`}
          items={shownCatalogs}
          selected={selectedCatalogsIds}
          renderItem={renderCatalog}
          onItemClick={handleCatalogClick}
          isMultiSelect={false}
          disableButtonClicks
        />
        <Divider />
      </FilterModal>
    )
  }

  const suffixText = getCatalogSuffixText(ROOT_CATALOG_ID)

  return (
    <>
      <FilterCell
        title={translate('catalog.name')}
        suffixText={suffixText || translate('catalog.all')}
        suffixTextTheme={suffixText ? 'primary' : undefined}
        onClick={handleFilterCellClick}
        onView={onFilterCellView}
        visible={isFilterCellVisible}
      />
      {openedCatalogsIds.map(renderCatalogFilterModal)}
    </>
  )
}

export default CatalogFilter
